import React, { useEffect, useId, useRef, useState, useContext, cloneElement, isValidElement } from 'react';
import type { FormItemProps, FieldData, FieldEntity, NotifyInfo, NamePath, Rule } from './types';
import { FormContext } from './FormContext';

const FormItem: React.FC<FormItemProps> = (props) => {
  const {
    name,
    label,
    children,
    rules = [],
    initialValue,
    trigger = 'onChange',
    validateTrigger,
    valuePropName = 'value',
    required,
    tooltipComponent,
    labelLayout = 'vertical',
    className = '',
    style,
    disabled: itemDisabled,
  } = props;

  const itemRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const context = useContext(FormContext);


  const { form } = context;
  const hasFormInstance = form && typeof form.getInternalHooks === 'function';
  const { registerField, subscribe } = hasFormInstance 
    ? (form.getInternalHooks('FORM_INTERNAL_HOOKS') || {})
    : {};
  const effectiveValidateTrigger = validateTrigger ?? context.validateTrigger ?? 'onChange';
  const [_, forceUpdate] = useState({});
  
  useEffect(() => {
    if (!name || !subscribe) return;
    
    const unsubscribe = subscribe(name, () => {
      forceUpdate({});
    });
    
    return unsubscribe;
  }, [name, subscribe]);

  const fieldRef = useRef<FieldEntity>({
    getNamePath: () => name!,
    props: props,
    onStoreChange: () => {},
    getElement: () => itemRef.current,
  });

  fieldRef.current.props = props;
  fieldRef.current.getNamePath = () => name!;
  fieldRef.current.getElement = () => itemRef.current;

  useEffect(() => {
    if (name && registerField) {
      const cancelRegister = registerField(fieldRef.current);
      return () => cancelRegister();
    }
  }, [name, registerField]);

  let fieldValue = name && hasFormInstance ? form.getFieldValue(name) : undefined;
  const fieldErrors = name && hasFormInstance ? form.getFieldError(name) : [];
  const isTouched = name && hasFormInstance ? form.isFieldTouched(name) : false;
  const isValidating = name && hasFormInstance ? form.isFieldValidating(name) : false;

  const hasError = fieldErrors.length > 0;
  let childNode: React.ReactNode = null;

  if (!hasFormInstance) {
    const child = typeof children === 'function' ? (children as any)() : children;
    return <div className={className} style={style}>{child}</div>;
  }

  if (typeof children !== 'function') {
    childNode = children;
  }

  if (name && isValidElement(children)) {
    const globalDisabled = context.disabled || itemDisabled;
    const childType = isValidElement(children) ? (children as any).type : null;
    const inputType = isValidElement(children) ? (children as any).props?.type : null;
    const isControlledInput = childType === 'input' && 
      inputType !== 'checkbox' && 
      inputType !== 'radio' && 
      inputType !== 'file';
    
    const normalizedValue = (fieldValue === undefined || fieldValue === null) && 
      valuePropName === 'value' && 
      isControlledInput
      ? ''
      : fieldValue;

    const controlProps: any = {
      [valuePropName]: normalizedValue,
      [trigger]: (...args: any[]) => {
        const originTrigger = (children as any).props[trigger];
        if (originTrigger) originTrigger(...args);

        const val = (args[0] && args[0].target && valuePropName === 'value')
          ? (args[0].target.type === 'checkbox' ? args[0].target.checked : args[0].target.value)
          : args[0];

        form.setFieldValue(name, val);

        if (effectiveValidateTrigger === trigger || (Array.isArray(effectiveValidateTrigger) && effectiveValidateTrigger.includes(trigger))) {
          form.validateFields([name]).catch(() => { });
        }
      },
      disabled: globalDisabled,
    };

    if (effectiveValidateTrigger === 'onBlur' || (Array.isArray(effectiveValidateTrigger) && effectiveValidateTrigger.includes('onBlur'))) {
      controlProps.onBlur = (...args: any[]) => {
        const originOnBlur = (children as any).props.onBlur;
        if (originOnBlur) originOnBlur(...args);
        form.validateFields([name]).catch(() => { });
      };
    }

    if (label) controlProps['aria-labelledby'] = labelId;
    if (typeof label === 'string') controlProps['aria-label'] = label;
    if (required) controlProps['aria-required'] = true;
    if (hasError) controlProps['aria-invalid'] = true;

    childNode = cloneElement(children as React.ReactElement, controlProps);
  } else if (typeof children === 'function' && name) {
    const fieldData: FieldData = {
      name,
      value: fieldValue,
      errors: fieldErrors,
      touched: isTouched,
      validating: isValidating
    };
    childNode = children(fieldData);
  } else if (typeof children === 'function') {
    const fieldData: FieldData = {
      name: [],
      value: undefined,
      errors: [],
      touched: false,
      validating: false
    };
    childNode = children(fieldData);
  }

  const isRequired = required || (rules && rules.some(r => r.required));
  const isHorizontal = labelLayout === 'horizontal';

  const labelClasses = isHorizontal
    ? 'block text-sm font-medium text-gray-700 mr-3 flex-shrink-0'
    : 'block text-sm font-medium text-gray-700 mb-1';

  const inputWrapperClasses = isHorizontal
    ? 'flex-1 flex flex-col'
    : 'w-full flex flex-col';

  const itemClasses = isHorizontal
  ? `flex flex-row space-x-3 items-center`
  : 'flex flex-col space-y-1';
  const wrapperClassName = [
    'form-item',
    itemClasses,
    hasError && isTouched ? 'form-item-has-error' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={itemRef}
      className={wrapperClassName}
      style={style}
      role={label ? 'group' : undefined}
      aria-labelledby={label ? labelId : undefined}
    >
      {label && (
        <label id={labelId} className={labelClasses}>
          {label}
          {isRequired && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
          {tooltipComponent && (
            <span className="ml-1">{tooltipComponent}</span>
          )}
        </label>
      )}
      <div className={inputWrapperClasses}>
        {childNode}
        {hasError && isTouched && (
          <p className="mt-1 text-xs text-red-600">
            {fieldErrors[0]}
          </p>
        )}
      </div>
    </div>
  );
};

export default FormItem;

